import { Injectable, Injector, NgZone } from '@angular/core';
import { sleep } from '@sama/js-core';
import { lastValueFrom } from 'rxjs';
import { mapTo, take } from 'rxjs/operators';
import { isPromise, whenAll } from '../misc-utilities/promise-utils';

@Injectable({ providedIn: 'root' })
export class BaseSaveManagerService {
  showIsSaving = false;
  errorOccurred = false;
  private handleError = true;
  private headOfIndicatorQueue = Promise.resolve();
  private ngZone: NgZone;

  constructor(injector: Injector) {
    this.ngZone = injector.get(NgZone);
  }

  withSaveIndicator(saveFn: () => Promise<void> | void, handleFailure = true) {
    this.onIndicatorQueue(() => {
      this.handleError = handleFailure;
      this.errorOccurred = false;
    });

    const result = saveFn();
    if (!isPromise(result)) {
      return Promise.resolve();
    }

    const nonThrowingResult = result.catch(() => {
      this.errorOccurred = true;
    });

    this.onIndicatorQueue(async () => {
      // If a save function finishes immediately, we assume it did not actually
      // do any work, so we do not want to show the save indicator. So we
      // watch for when the microtask queue is empty (i.e. when the zone is
      // stable), and if the save function already finished we skip the
      // indicator.
      const zoneStableResult = Symbol();
      const zoneStablePromise = lastValueFrom(
        this.ngZone.onStable.pipe(take(1), mapTo(zoneStableResult)),
      );
      const winner = await Promise.race([zoneStablePromise, nonThrowingResult]);
      if (winner !== zoneStableResult) {
        return;
      }

      this.showIsSaving = true;
      // ensure we always wait at least until the save indicator animates onto
      // the screen before handling success/failure
      void whenAll([nonThrowingResult, sleep(200)]).then(() => {
        this.showIsSaving = false;
      });
    });

    return nonThrowingResult;
  }

  indicatorShouldHide() {
    return this.errorOccurred && !this.handleError;
  }

  // the save indicator works on a separate queue so that it can wait for
  // animations to complete before changing what is displayed
  private onIndicatorQueue(fn: any) {
    this.headOfIndicatorQueue = this.headOfIndicatorQueue.then(fn, fn);
  }
}
